import React, { useState, useEffect } from 'react';
import { PresenceGrid } from './components/PresenceGrid';
import { SocialIntent, VisibilityScope } from './types/SocialEvent';
import type { SocialEvent } from './types/SocialEvent';
import { startOfDay, addHours } from 'date-fns';

const demoEvents: SocialEvent[] = [
  {
    id: 'demo-1',
    title: 'Focus Time (Deep Work)',
    startTime: addHours(startOfDay(new Date()), 9).getTime(),
    endTime: addHours(startOfDay(new Date()), 12).getTime(),
    intent: SocialIntent.BUSY,
    visibilityScope: VisibilityScope.PRIVATE,
    currentParticipants: ['me'],
  },
  {
    id: 'demo-2',
    title: 'Open Coffee Chat ☕️',
    startTime: addHours(startOfDay(new Date()), 14).getTime(),
    endTime: addHours(startOfDay(new Date()), 15).getTime(),
    intent: SocialIntent.OPEN_INVITE,
    visibilityScope: VisibilityScope.EXTENDED,
    currentParticipants: ['friend1', 'friend2'],
    maxParticipants: 5,
  },
  {
    id: 'demo-3',
    title: 'Need a Tennis Partner 🎾',
    startTime: addHours(startOfDay(new Date()), 17).getTime(),
    endTime: addHours(startOfDay(new Date()), 19).getTime(),
    intent: SocialIntent.CATALYST,
    visibilityScope: VisibilityScope.EXTENDED,
    currentParticipants: ['me'],
  },
  {
    id: 'demo-4',
    title: 'Free / Suggest Anything',
    startTime: addHours(startOfDay(new Date()), 20).getTime(),
    endTime: addHours(startOfDay(new Date()), 23).getTime(),
    intent: SocialIntent.BEACON,
    visibilityScope: VisibilityScope.EXTENDED,
    currentParticipants: ['me'],
  }
];

export default function PresenceGridDemo() {
  const [events, setEvents] = useState<SocialEvent[]>(demoEvents);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const now = Math.floor(Date.now() / 1000);
        const start = now - (24 * 60 * 60);
        const end = now + (7 * 24 * 60 * 60);
        const response = await fetch(`/wythe/v1/event?start=${start}&end=${end}`);
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 0) {
            // Map backend fields to frontend SocialEvent interface
            const mapped: SocialEvent[] = data.map((e: any) => ({
              id: e.id || e.ID,
              title: e.displayTitle || e.title || e.Title,
              startTime: e.startTime || e.StartTime,
              endTime: e.endTime || e.EndTime,
              intent: e.intent || e.Intent || SocialIntent.BUSY,
              visibilityScope: e.visibilityScope || e.VisibilityScope || VisibilityScope.PRIVATE,
              currentParticipants: e.attendeeIds || e.AttendeeIDs || [],
            }));
            setEvents([...demoEvents, ...mapped]);
          }
        }
      } catch (error) {
        console.error("Failed to fetch real events for demo", error);
      }
    };
    fetchEvents();
  }, []);

  const handleEventClick = (event: SocialEvent) => {
    alert(`Clicked event: ${event.title}`);
  };

  const handleJoinEvent = (event: SocialEvent) => {
    alert(`Joined event: ${event.title}`);
  };

  return (
    <div className="p-8 bg-zinc-100 min-h-screen">
      <div className="max-w-6xl mx-auto h-[800px] flex flex-col space-y-4">
        <div className="flex justify-between items-end">
          <div>
            <h1 className="text-3xl font-bold text-zinc-900 tracking-tight">Social Presence</h1>
            <p className="text-zinc-500">Visualizing intent over time.</p>
          </div>
          <div className="flex space-x-2 pb-1">
             <span className="px-2 py-1 bg-slate-900 text-white text-[10px] rounded font-bold">BUSY</span>
             <span className="px-2 py-1 border-2 border-indigo-500 text-indigo-700 text-[10px] rounded font-bold">OPEN INVITE</span>
             <span className="px-2 py-1 border-2 border-dashed border-emerald-500 text-emerald-700 text-[10px] rounded font-bold">CATALYST</span>
             <span className="px-2 py-1 bg-gradient-to-r from-purple-400 to-pink-500 text-white text-[10px] rounded font-bold">BEACON</span>
          </div>
        </div>
        
        <div className="flex-1 min-h-0">
          <PresenceGrid 
            events={events} 
            onEventClick={handleEventClick}
            onJoinEvent={handleJoinEvent}
          />
        </div>
      </div>
    </div>
  );
}
